import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../lib/api";
import { formatMoney } from "../lib/engage";
import { useUnits } from "../hooks/useUnits";
import { fmtWeight } from "../lib/units";
import { useToast } from "./Toast";
import { Avatar, photoToAvatar } from "./Avatar";

// The crew member's own extras on the home screen: profile picture, what
// they're doing when not trimming, contract offers, and feedback on their runs.

export const STATUS_LABEL: Record<string, string> = {
  break: "On break", waiting: "Waiting for product", bagging: "Bagging up", cleaning: "Resetting / cleaning",
};
// Button text: short enough for four across on a phone.
const STATUS_CHIP: Record<string, string> = { break: "Break", waiting: "Waiting", bagging: "Bagging", cleaning: "Cleaning" };

async function errText(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({})) as { error?: string };
  return body.error ?? fallback;
}

function elapsed(fromMs: number): string {
  const m = Math.max(0, Math.floor((Date.now() - fromMs) / 60000));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
}

export function ProfilePicture() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const { data } = useQuery({
    queryKey: ["me-profile"],
    queryFn: () => api.get("/api/me/profile").then((r) => readJson<{ id: number; name: string; avatar_at: number | null }>(r)),
  });
  if (!data) return null;

  async function pick(file: File | undefined) {
    if (!file) return;
    try {
      const data_url = await photoToAvatar(file);
      const res = await api.putRaw("/api/me/avatar", JSON.stringify({ data_url }), { "Content-Type": "application/json" });
      if (!res.ok) { toast(await errText(res, "Couldn't save the picture"), "err"); return; }
      await queryClient.invalidateQueries({ queryKey: ["me-profile"] });
      toast("Picture updated", "ok");
    } catch {
      toast("Couldn't read that picture", "err");
    }
  }

  return (
    <button type="button" className="avatar-button" data-testid="profile-picture" aria-label="Change your profile picture" onClick={() => input.current?.click()}>
      <Avatar playerId={data.id} name={data.name} avatarAt={data.avatar_at} size={56} />
      <span className="avatar-edit" aria-hidden="true">✎</span>
      <input ref={input} type="file" accept="image/*" hidden data-testid="profile-picture-input" onChange={(e) => void pick(e.target.files?.[0])} />
    </button>
  );
}

/** Not trimming? Pick what you're doing so the day's time adds up. Starting a trim ends it. */
export function StatusBar() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [, tick] = useState(0);
  const { data } = useQuery({
    queryKey: ["me-status"],
    queryFn: () => api.get("/api/me/status").then((r) => readJson<{ status: string | null; started_at: number | null }>(r)),
    refetchInterval: 60_000,
  });
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  async function set(status: string | null) {
    const res = await api.postJson("/api/me/status", { status });
    if (!res.ok) { toast(await errText(res, "Couldn't change status"), "err"); return; }
    await queryClient.invalidateQueries({ queryKey: ["me-status"] });
  }

  const current = data?.status ?? null;
  return (
    <div className="panel" data-testid="status-bar">
      <h2>
        {current ? STATUS_LABEL[current] : "Not trimming?"}
        {current && data?.started_at && <span className="pill live-pill" data-testid="status-current">{elapsed(data.started_at)}</span>}
      </h2>
      <div className="chip-grid">
        {Object.entries(STATUS_LABEL).map(([key, label]) => (
          <button
            key={key} type="button" data-testid={`status-${key}`} aria-label={label}
            className={`chip${current === key ? " active" : ""}`} aria-pressed={current === key}
            onClick={() => void set(current === key ? null : key)}
          >
            {STATUS_CHIP[key]}
          </button>
        ))}
      </div>
      {current && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} data-testid="status-clear" onClick={() => void set(null)}>
          Back to work
        </button>
      )}
    </div>
  );
}

type MyContract = {
  id: number; status: "proposed" | "accepted"; target_grams: number; amount_cents: number; start_day: string; end_day: string | null;
  daily_target_grams: number | null; daily_bonus_cents: number | null; min_qa_score: number | null; paid_at: number | null;
  grams: number; complete: boolean;
};

export function ContractOffers() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const units = useUnits();
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["me-contracts"],
    queryFn: () => api.get("/api/me/contracts").then((r) => readJson<{ currency: string; contracts: MyContract[] }>(r)),
  });
  const shown = (data?.contracts ?? []).filter((k) => k.status === "proposed" || !k.paid_at);
  if (!data || !shown.length) return null;
  const money = (c: number) => formatMoney(c, data.currency);

  async function respond(k: MyContract, accept: boolean) {
    if (!accept && !confirm("Decline this contract? Your manager will see that you declined.")) return;
    setBusy(true);
    try {
      const res = await api.postJson(`/api/me/contracts/${k.id}/respond`, { accept });
      if (!res.ok) { toast(await errText(res, "Couldn't answer"), "err"); return; }
      await queryClient.invalidateQueries({ queryKey: ["me-contracts"] });
      toast(accept ? "Contract accepted" : "Contract declined", "ok");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel" data-testid="my-contracts">
      <h2>Contracts</h2>
      {shown.map((k) => (
        <div key={k.id} className="panel" data-testid={`my-contract-${k.id}`} style={{ marginBottom: 8 }}>
          <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <b>Trim {fmtWeight(k.target_grams, units, 0)} for {money(k.amount_cents)}</b>
            <span className={`pill${k.status === "proposed" ? " live-pill" : ""}`} style={{ marginLeft: "auto" }}>
              {k.status === "proposed" ? "New offer" : k.complete ? "Target reached" : "In progress"}
            </span>
          </div>
          <div className="small muted">
            {k.start_day} to {k.end_day ?? "no end date"}
            {k.daily_bonus_cents != null && ` · plus ${money(k.daily_bonus_cents)} on days you trim ${fmtWeight(k.daily_target_grams ?? 0, units, 0)} with every run scoring QA ${k.min_qa_score}+`}
          </div>
          {k.status === "proposed" ? (
            <>
              <div className="small muted" style={{ margin: "6px 0" }}>While it runs, your work on these days is paid by this contract instead of hourly or per gram.</div>
              <div className="btn-row">
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy} data-testid={`decline-contract-${k.id}`} onClick={() => void respond(k, false)}>Decline</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={busy} data-testid={`accept-contract-${k.id}`} onClick={() => void respond(k, true)}>Accept</button>
              </div>
            </>
          ) : (
            <>
              <progress value={Math.min(k.grams, k.target_grams)} max={k.target_grams} style={{ width: "100%" }} />
              <div className="small muted">{fmtWeight(k.grams, units, 0)} of {fmtWeight(k.target_grams, units, 0)}</div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

type FeedbackItem = {
  id: number; author: string; rating: 1 | -1 | null; body: string | null; created_at: number; seen_at: number | null;
  run_started_at: number; run_grams: number | null;
};

export function FeedbackInbox() {
  const units = useUnits();
  const { data } = useQuery({
    queryKey: ["me-feedback"],
    queryFn: () => api.get("/api/me/feedback").then((r) => readJson<{ unseen: number; feedback: FeedbackItem[] }>(r)),
    refetchInterval: 120_000,
  });
  // Shown = read: mark it on the server, but keep the "new" marks on screen for this visit.
  const unseen = data?.unseen ?? 0;
  useEffect(() => {
    if (unseen) void api.postJson("/api/me/feedback/seen", {});
  }, [unseen]);
  if (!data?.feedback.length) return null;

  return (
    <div className="panel" data-testid="my-feedback">
      <h2>Feedback {unseen > 0 && <span className="pill live-pill" data-testid="feedback-unseen">{unseen} new</span>}</h2>
      {data.feedback.slice(0, 5).map((f) => (
        <div key={f.id} className="mini-run" data-testid="feedback-item">
          <div>
            <div className="who">
              {f.rating === 1 ? "👍 " : f.rating === -1 ? "👎 " : ""}{f.body ?? (f.rating === 1 ? "Good run" : "Needs work")}
              {f.seen_at == null && <span className="pill live-pill" style={{ marginLeft: 6 }}>new</span>}
            </div>
            <div className="meta">
              {f.author} · run of {new Date(f.run_started_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              {f.run_grams != null ? `, ${fmtWeight(f.run_grams, units, 0)}` : ""}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
